const SITE_NAME = 'Else Ringnalda'
// Production domain per the Fly.io certificates (elseringnalda.nl). Meta
// functions also run in the browser, so guard the process access.
const BASE_URL =
	(typeof process !== 'undefined' && process.env.BASE_URL) ||
	'https://elseringnalda.nl'
const DEFAULT_OG_IMAGE = `${BASE_URL}/img/og-default.jpg`

// App language code -> Open Graph locale
const OG_LOCALES = {
	nl: 'nl_NL',
	vl: 'nl_BE',
	fr: 'fr_FR',
	en: 'en_US',
	de: 'de_DE',
	ja: 'ja_JP',
} as const

interface BuildMetaOptions {
	title: string
	description: string
	url: string
	image?: string | null
	imageAlt?: string | null
	type?: 'website' | 'article'
	publishedAt?: string | null
	noIndex?: boolean
	/** App language code ('nl', 'vl', 'fr', 'en', 'de', 'ja'). Defaults to 'nl'. */
	locale?: string
}

/**
 * Derive a meta description from (possibly WYSIWYG-HTML) CMS content: strips
 * tags, collapses whitespace and cuts at ~155 chars on a word boundary.
 */
export function metaDescriptionFromContent(html: string, maxLength = 155) {
	const text = html
		.replace(/<[^>]*>/g, ' ')
		.replace(/&nbsp;/gi, ' ')
		.replace(/&amp;/gi, '&')
		.replace(/&quot;/gi, '"')
		.replace(/&#0?39;|&apos;/gi, "'")
		.replace(/&lt;/gi, '<')
		.replace(/&gt;/gi, '>')
		.replace(/\s+/g, ' ')
		.trim()
	if (text.length <= maxLength) return text
	const sliced = text.slice(0, maxLength)
	const lastSpace = sliced.lastIndexOf(' ')
	return `${(lastSpace > 0 ? sliced.slice(0, lastSpace) : sliced).trimEnd()}…`
}

export function buildMeta(options: BuildMetaOptions) {
	const fullUrl = options.url.startsWith('http')
		? options.url
		: `${BASE_URL}${options.url}`
	const rawImage = options.image || DEFAULT_OG_IMAGE
	const ogImage = rawImage.startsWith('http')
		? rawImage
		: `${BASE_URL}${rawImage}`
	const fullTitle = options.title.includes(SITE_NAME)
		? options.title
		: `${options.title} — ${SITE_NAME}`
	const language = (options.locale ?? 'nl') as keyof typeof OG_LOCALES
	const ogLocale = OG_LOCALES[language] ?? OG_LOCALES.nl

	return [
		{ title: fullTitle },
		{ name: 'description', content: options.description },

		{ property: 'og:title', content: options.title },
		{ property: 'og:description', content: options.description },
		{ property: 'og:url', content: fullUrl },
		{ property: 'og:type', content: options.type || 'website' },
		{ property: 'og:site_name', content: SITE_NAME },
		{ property: 'og:locale', content: ogLocale },
		...Object.values(OG_LOCALES)
			.filter((l) => l !== ogLocale)
			.map((l) => ({ property: 'og:locale:alternate', content: l })),
		{ property: 'og:image', content: ogImage },
		{ property: 'og:image:width', content: '1200' },
		{ property: 'og:image:height', content: '630' },
		{ property: 'og:image:alt', content: options.imageAlt || options.title },

		{
			name: 'twitter:card',
			content: ogImage ? 'summary_large_image' : 'summary',
		},
		{ name: 'twitter:title', content: options.title },
		{ name: 'twitter:description', content: options.description },
		{ name: 'twitter:image', content: ogImage },

		// The site serves every language on the same URL (cookie-based i18n),
		// so per-language hreflang alternates would all point at the canonical
		// URL — which is meaningless to crawlers. Canonical + og:locale is the
		// correct signal here; hreflang belongs to per-language URLs only.
		{ tagName: 'link' as const, rel: 'canonical', href: fullUrl },

		...(options.publishedAt
			? [
					{
						property: 'article:published_time',
						content: options.publishedAt,
					},
				]
			: []),

		...(options.noIndex
			? [{ name: 'robots', content: 'noindex, nofollow' }]
			: []),
	]
}

export function buildWebSiteJsonLd() {
	return {
		'script:ld+json': {
			'@context': 'https://schema.org',
			'@type': 'WebSite',
			name: SITE_NAME,
			url: BASE_URL,
			inLanguage: 'nl',
		},
	}
}

export function buildOrganizationJsonLd() {
	return {
		'script:ld+json': {
			'@context': 'https://schema.org',
			'@type': 'Person',
			name: 'Else Ringnalda',
			url: BASE_URL,
			jobTitle: 'Beeldend Kunstenaar',
			image: `${BASE_URL}/img/og-default.jpg`,
			sameAs: [] as string[],
		},
	}
}

export function buildCreativeWorkJsonLd(work: {
	title: string
	description: string
	url: string
	image?: string | null
	dateCreated?: string | null
}) {
	return {
		'script:ld+json': {
			'@context': 'https://schema.org',
			'@type': 'VisualArtwork',
			name: work.title,
			description: work.description,
			url: `${BASE_URL}${work.url}`,
			...(work.image
				? {
						image: work.image.startsWith('http')
							? work.image
							: `${BASE_URL}${work.image}`,
					}
				: {}),
			...(work.dateCreated
				? { dateCreated: work.dateCreated }
				: {}),
			creator: {
				'@type': 'Person',
				name: 'Else Ringnalda',
				url: BASE_URL,
			},
			artform: 'Sculpture',
		},
	}
}

export function buildBreadcrumbJsonLd(
	items: Array<{ name: string; url: string }>,
) {
	return {
		'script:ld+json': {
			'@context': 'https://schema.org',
			'@type': 'BreadcrumbList',
			itemListElement: items.map((item, i) => ({
				'@type': 'ListItem',
				position: i + 1,
				name: item.name,
				item: `${BASE_URL}${item.url}`,
			})),
		},
	}
}
